import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useGuides from '../hooks/useGuides';
import { RATES, fmt, BUDGET_STYLES, BUDGET_EXTRAS } from '../data/tools';
import renderGuide from '../utils/renderGuide';
import '../styles/Dashboard.css';

const TABS = [
  { id: 'overview',   label: { en: 'Overview', fr: 'Aperçu' }, icon: '🏠' },
  { id: 'guides',     label: { en: 'All Guides', fr: 'Tous les guides' }, icon: '📚' },
  { id: 'budget',     label: { en: 'Budget Calculator', fr: 'Calcul du budget' }, icon: '🧮' },
  { id: 'checklist',  label: { en: 'Checklist', fr: 'Liste de contrôle' }, icon: '✅' },
  { id: 'prices',     label: { en: 'Price List', fr: 'Liste de prix' }, icon: '💰' },
  { id: 'itinerary',  label: { en: 'Itineraries', fr: 'Itinéraires' }, icon: '📅' },
  { id: 'districts',  label: { en: 'Districts', fr: 'Quartiers' }, icon: '🗺️' },
  { id: 'profile',    label: { en: 'Profile', fr: 'Profil' }, icon: '👤' },
];

const CHECKLIST_ITEMS = [
  { id: 'passport',   en: 'Passport valid 6+ months after return', fr: 'Passeport valide 6+ mois après le retour' },
  { id: 'visa',       en: 'Visa obtained (evisa.gov.tr or embassy only)', fr: 'Visa obtenu (evisa.gov.tr ou ambassade uniquement)' },
  { id: 'insurance',  en: 'Travel insurance (€30,000+) printed', fr: 'Assurance voyage (30 000 €+) imprimée' },
  { id: 'hotel',      en: 'Hotel within 5 min of T1 tram or M2 metro', fr: 'Hôtel à moins de 5 min du tram T1 ou du métro M2' },
  { id: 'itinerary',  en: 'Day-by-day itinerary chosen (3, 5 or 7 days)', fr: 'Itinéraire jour par jour choisi (3, 5 ou 7 jours)' },
  { id: 'museumpass', en: 'Decided: Museum Pass (€105) or single tickets', fr: 'Choix fait : Museum Pass (105 €) ou billets simples' },
  { id: 'euros',      en: 'Euros in cash + card that works abroad', fr: 'Euros en espèces + carte valable à l\'étranger' },
  { id: 'esim',       en: 'eSIM or SIM plan ready', fr: 'eSIM ou forfait SIM prêt' },
  { id: 'apps',       en: 'Apps installed: BiTaksi/Uber, maps, translator', fr: 'Applis installées : BiTaksi/Uber, cartes, traducteur' },
  { id: 'modest',     en: 'Modest clothes / scarf for mosque visits', fr: 'Tenue correcte / foulard pour les mosquées' },
  { id: 'bosphorus',  en: 'Bosphorus cruise day planned (Şehir Hatları)', fr: 'Journée croisière Bosphore prévue (Şehir Hatları)' },
  { id: 'taxfree',    en: 'Kept receipts & tax-free forms for the airport', fr: 'Reçus & formulaires de détaxe gardés pour l\'aéroport' },
];


const PRICE_DATA = [
  { item: { en: 'Metro / tram / bus ride', fr: 'Trajet métro / tram / bus' }, tl: '42', xaf: '490' },
  { item: { en: 'Simit (sesame bread ring)', fr: 'Simit (pain au sésame)' }, tl: '30–50', xaf: '350–600' },
  { item: { en: 'Glass of Turkish tea', fr: 'Verre de thé turc' }, tl: '20–40', xaf: '230–470' },
  { item: { en: 'Bottle of water (0.5 L)', fr: 'Bouteille d\'eau (0,5 L)' }, tl: '15–25', xaf: '180–290' },
  { item: { en: 'Turkish coffee', fr: 'Café turc' }, tl: '80–150', xaf: '940–1,750' },
  { item: { en: 'Döner sandwich (street)', fr: 'Sandwich döner (rue)' }, tl: '100–150', xaf: '1,200–1,750' },
  { item: { en: 'Kebab plate with rice & salad (local)', fr: 'Assiette kebab riz & salade (local)' }, tl: '350–500', xaf: '4,100–5,900' },
  { item: { en: 'Fish sandwich (Eminönü)', fr: 'Sandwich au poisson (Eminönü)' }, tl: '200–300', xaf: '2,300–3,500' },
  { item: { en: 'Home-style meal (lokanta)', fr: 'Repas maison (lokanta)' }, tl: '300–500', xaf: '3,500–5,900' },
  { item: { en: 'Bosphorus short tour (Şehir Hatları)', fr: 'Tour court du Bosphore (Şehir Hatları)' }, tl: '~340', xaf: '~4,000' },
  { item: { en: 'Restaurant dinner, mid-range (per person)', fr: 'Dîner restaurant milieu de gamme (par pers.)' }, tl: '800–1,500', xaf: '9,400–17,500' },
  { item: { en: 'Neighbourhood hammam', fr: 'Hammam de quartier' }, tl: '1,500–4,000', xaf: '17,500–47,000' },
  { item: { en: 'Historic hammam (Çemberlitaş, scrub & foam)', fr: 'Hammam historique (Çemberlitaş, gommage & mousse)' }, tl: '~3,800 (€68)', xaf: '~44,600' },
  { item: { en: 'Cappadocia flight, one way', fr: 'Vol vers la Cappadoce, aller simple' }, tl: '1,700–3,900 (€30–70)', xaf: '19,700–45,900' },
  { item: { en: 'Tourist SIM (Turkcell, city store)', fr: 'SIM touriste (Turkcell, boutique)' }, tl: '~1,300', xaf: '~15,200' },
];

const ITINERARY = {
  3: [
    { day: 1, en: 'Sultanahmet: Hagia Sophia (09:00), Blue Mosque, Basilica Cistern, Galata Bridge at sunset', fr: 'Sultanahmet : Sainte-Sophie (9h), Mosquée Bleue, Citerne Basilique, pont de Galata au coucher du soleil' },
    { day: 2, en: 'Topkapı Palace + Harem, Grand Bazaar (not Sunday), Süleymaniye, Spice Bazaar', fr: 'Palais de Topkapı + Harem, Grand Bazar (pas le dimanche), Süleymaniye, Bazar aux épices' },
    { day: 3, en: 'Şehir Hatları Bosphorus tour, Karaköy lunch, Galata Tower, Istiklal & Taksim', fr: 'Croisière Şehir Hatları, déjeuner à Karaköy, Tour de Galata, Istiklal & Taksim' },
  ],
  5: [
    { day: 4, en: 'Dolmabahçe Palace, ferry to Kadıköy, Moda seafront, sunset in Üsküdar', fr: 'Palais de Dolmabahçe, ferry vers Kadıköy, front de mer de Moda, coucher de soleil à Üsküdar' },
    { day: 5, en: 'Princes\' Islands (Büyükada) by ferry — go on a weekday', fr: 'Îles des Princes (Büyükada) en ferry — en semaine de préférence' },
  ],
  7: [
    { day: 6, en: 'Balat & Fener, Kariye (Chora), Eyüp Sultan & Pierre Loti cable car', fr: 'Balat & Fener, Kariye (Chora), Eyüp Sultan & téléphérique Pierre Loti' },
    { day: 7, en: 'Day trip to Bursa — or shopping in Laleli, Osmanbey or a big mall', fr: 'Excursion à Bursa — ou shopping à Laleli, Osmanbey ou un grand centre commercial' },
  ],
};

const DISTRICT_DATA = [
  { name: 'Sultanahmet', area: { en: 'Historic Peninsula', fr: 'Péninsule historique' }, hotel: '€60–150', line: 'T1', best: { en: 'Monuments', fr: 'Monuments' }, note: { en: 'Walk to Hagia Sophia and Topkapı. Quiet at night; watch for carpet-shop touts.', fr: 'Sainte-Sophie et Topkapı à pied. Calme le soir ; attention aux rabatteurs.' } },
  { name: 'Karaköy / Galata', area: { en: 'Beyoğlu', fr: 'Beyoğlu' }, hotel: '€90–200', line: 'T1 / M2', best: { en: 'Cafés & views', fr: 'Cafés & vues' }, note: { en: 'Trendy cafés, design hotels, waterfront. Steep streets.', fr: 'Cafés branchés, hôtels design, bord de l\'eau. Rues en pente.' } },
  { name: 'Taksim / Istiklal', area: { en: 'Beyoğlu', fr: 'Beyoğlu' }, hotel: '€60–160', line: 'M2 / HVL-9', best: { en: 'Nightlife & airport bus', fr: 'Vie nocturne & bus aéroport' }, note: { en: 'Direct Havaist bus to IST. Lively and noisy; avoid "friendly" bar invitations.', fr: 'Bus Havaist direct vers IST. Animé et bruyant ; évitez les invitations dans les bars.' } },
  { name: 'Beşiktaş', area: { en: 'European shore', fr: 'Rive européenne' }, hotel: '€80–180', line: { en: 'Ferry', fr: 'Ferry' }, best: { en: 'Local life', fr: 'Vie locale' }, note: { en: 'Dolmabahçe Palace, fish market, ferries to Asia. Student energy.', fr: 'Palais de Dolmabahçe, marché aux poissons, ferries vers l\'Asie. Ambiance étudiante.' } },
  { name: 'Kadıköy / Moda', area: { en: 'Asian side', fr: 'Rive asiatique' }, hotel: '€50–120', line: 'M4 / Ferry', best: { en: 'Food & value', fr: 'Cuisine & prix' }, note: { en: 'Best food market, cheaper restaurants, 20-min ferry to Eminönü.', fr: 'Meilleur marché alimentaire, restaurants moins chers, 20 min de ferry vers Eminönü.' } },
  { name: 'Üsküdar', area: { en: 'Asian side', fr: 'Rive asiatique' }, hotel: '€50–110', line: 'Marmaray / M5', best: { en: 'Mosques & sunsets', fr: 'Mosquées & couchers de soleil' }, note: { en: 'Conservative and calm; view of the Maiden\'s Tower. Marmaray to the old city.', fr: 'Conservateur et calme ; vue sur la Tour de Léandre. Marmaray vers la vieille ville.' } },
  { name: 'Laleli / Aksaray', area: { en: 'Historic Peninsula', fr: 'Péninsule historique' }, hotel: '€35–90', line: 'T1 / M1', best: { en: 'Wholesale & budget', fr: 'Gros & petit budget' }, note: { en: 'Wholesale clothing, cargo agents, African community. Busy and less scenic.', fr: 'Vêtements en gros, transitaires, communauté africaine. Animé et moins pittoresque.' } },
  { name: 'Şişli / Osmanbey', area: { en: 'Business district', fr: 'Quartier d\'affaires' }, hotel: '€60–150', line: 'M2', best: { en: 'Fashion wholesale', fr: 'Mode en gros' }, note: { en: 'Osmanbey wholesale fashion, Cevahir mall, business hotels.', fr: 'Mode en gros à Osmanbey, centre Cevahir, hôtels d\'affaires.' } },
];

const pick = (v, lang) => (typeof v === 'object' ? v[lang] || v.en : v);

const PremiumDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const lang = localStorage.getItem('ig_language') || 'en';
  const { guides, loading: guidesLoading, error: guidesError, expired, lockedAt } = useGuides();
  const guideStatus = guidesLoading
    ? <p className="db-panel-note">⏳ {lang === 'fr' ? 'Chargement des guides…' : 'Loading guides…'}</p>
    : guidesError
      ? <p className="db-panel-note">⚠️ {lang === 'fr' ? 'Impossible de charger les guides. Vérifiez votre connexion et rechargez la page.' : 'Could not load guides. Check your connection and reload the page.'}</p>
      : expired
        ? <p className="db-panel-note">⌛ {lang === 'fr' ? 'Votre accès a expiré. Renouvelez votre forfait pour retrouver tous vos guides.' : 'Your access has expired. Renew your plan to get all your guides back.'}</p>
        : null;
  const PREMIUM_GUIDES = guides;

  const [tab, setTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [tripDays, setTripDays] = useState(3);
  const [budget, setBudget] = useState({ days: 5, people: 1, style: 'mid', extras: { airport: true, hagia: true, cistern: true, bosphorus: true, sim: true } });
  const setB = (patch) => setBudget(b => ({ ...b, ...patch }));
  const [checked, setChecked] = useState(() => {
    const s = localStorage.getItem('ig_checklist_nav');
    return s ? JSON.parse(s) : {};
  });

  const toggleCheck = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem('ig_checklist_nav', JSON.stringify(next));
  };

  const completedCount = Object.values(checked).filter(Boolean).length;

  const OverviewTab = () => (
    <div className="db-overview">
      <div className="db-welcome-card premium">
        <div className="db-welcome-icon">🧭</div>
        <div>
          <h2>{lang === 'fr' ? `Bienvenue, ${user?.firstName || 'Navigateur'} !` : `Welcome back, ${user?.firstName || 'Navigator'}!`}</h2>
          <p>{lang === 'fr' ? `Accès complet aux ${PREMIUM_GUIDES.length} guides de voyage et aux outils Navigateur.` : `Full access to all ${PREMIUM_GUIDES.length} travel guides and Navigator tools.`}</p>
        </div>
        <div className="db-plan-badge premium">{lang === 'fr' ? 'Navigateur' : 'Navigator'}</div>
      </div>

      <div className="db-stats-row">
        <div className="db-stat-card">
          <div className="db-stat-num">{PREMIUM_GUIDES.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Guides Disponibles' : 'Guides Available'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">{completedCount}/{CHECKLIST_ITEMS.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Tâches Complétées' : 'Tasks Completed'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">90</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Jours d\'Accès' : 'Days of Access'}</div>
        </div>
      </div>

      <div className="db-info-panel">
        <h3>💱 {lang === 'fr' ? 'Change — septembre 2026' : 'Currency — September 2026'}</h3>
        <p className="db-panel-note">{lang === 'fr' ? 'Apportez des euros : le taux euro–XAF est fixe (1 € = 655,957 XAF).' : 'Bring euros: the euro–XAF rate is fixed (€1 = 655.957 XAF).'}</p>
        <div className="db-fact-grid">
          <div className="db-fact"><span>≈ 56 TL</span><p>= 1 €</p></div>
          <div className="db-fact"><span>≈ 85 TL</span><p>= 1 000 XAF</p></div>
          <div className="db-fact"><span>≈ 1 170 XAF</span><p>= 100 TL</p></div>
          <div className="db-fact"><span>TL</span><p>{lang === 'fr' ? 'Payez toujours en livres' : 'Always pay in lira'}</p></div>
        </div>
      </div>

      <div className="db-info-panel green">
        <h3>⛴️ {lang === 'fr' ? 'Bosphore & îles — prix locaux' : 'Bosphorus & Islands — Local Prices'}</h3>
        <div className="db-fact-grid">
          <div className="db-fact"><span>~340 TL</span><p>{lang === 'fr' ? 'Tour court (2 h)' : 'Short tour (2 h)'}</p></div>
          <div className="db-fact"><span>~640 TL</span><p>{lang === 'fr' ? 'Grand tour (6 h)' : 'Long tour (6 h)'}</p></div>
          <div className="db-fact"><span>~59 TL</span><p>{lang === 'fr' ? 'Ferry Eminönü–Kadıköy' : 'Ferry Eminönü–Kadıköy'}</p></div>
          <div className="db-fact"><span>~156 TL</span><p>{lang === 'fr' ? 'Ferry Bostancı–Îles' : 'Ferry Bostancı–Islands'}</p></div>
        </div>
      </div>

      <div className="db-table-panel">
        <h3>🎟️ {lang === 'fr' ? 'Museum Pass : rentable ou pas ?' : 'Museum Pass: Worth It or Not?'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Votre programme' : 'Your plan'}</th><th>{lang === 'fr' ? 'Billets simples' : 'Single tickets'}</th><th>{lang === 'fr' ? 'Verdict' : 'Verdict'}</th></tr></thead>
            <tbody>
              <tr><td>Topkapı + Harem</td><td>~€49</td><td>❌ {lang === 'fr' ? 'Billet simple' : 'Single ticket'}</td></tr>
              <tr><td>Topkapı + {lang === 'fr' ? 'Tour de Galata' : 'Galata Tower'}</td><td>~€79</td><td>❌ {lang === 'fr' ? 'Billets simples' : 'Single tickets'}</td></tr>
              <tr><td>Topkapı + Galata + {lang === 'fr' ? 'Archéologie' : 'Archaeology'} + {lang === 'fr' ? 'Arts islamiques' : 'Islamic Arts'}</td><td>~€111</td><td>✅ Museum Pass (€105)</td></tr>
            </tbody>
          </table>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'Le pass n\'inclut ni Sainte-Sophie, ni la Citerne Basilique, ni Dolmabahçe.' : 'The pass does not include Hagia Sophia, the Basilica Cistern or Dolmabahçe.'}</p>
      </div>

      <div className="db-upgrade-banner enterprise">
        <div>
          <h4>{lang === 'fr' ? 'Passez à Pionnier — outils affaires' : 'Upgrade to Pathfinder — Business Tools'}</h4>
          <p>{lang === 'fr' ? 'Sourcing en gros, calendrier des salons, créer une société, permis de travail, achat immobilier, banque, séjour & citoyenneté.' : 'Wholesale sourcing, trade-fair calendar, company setup, work permits, buying property, banking, residence & citizenship.'}</p>
        </div>
        <button className="db-upgrade-btn enterprise" onClick={() => navigate('/plans')}>
          {lang === 'fr' ? '⬆️ Passer à Pionnier' : '⬆️ Upgrade to Pathfinder'}
        </button>
      </div>
    </div>
  );

  const GuidesTab = () => (
    <div className="db-guides">
      {selectedGuide ? (
        <div className="db-guide-reader">
          <button className="db-back-btn" onClick={() => setSelectedGuide(null)}>← {lang === 'fr' ? 'Retour' : 'Back'}</button>
          <div className="db-guide-icon-lg">{selectedGuide.icon}</div>
          <h2>{selectedGuide.title[lang] || selectedGuide.title.en}</h2>
          <div className="db-guide-content" dangerouslySetInnerHTML={{ __html: renderGuide(selectedGuide.content[lang] || selectedGuide.content.en) }} />
        </div>
      ) : (
        <>
          <h2 className="db-section-title">{lang === 'fr' ? 'Tous vos guides de voyage' : 'All Your Travel Guides'}</h2>
          {guideStatus}
          <div className="db-guides-grid">
            {PREMIUM_GUIDES.map(guide => (
              <div key={guide.id} className="db-guide-card" onClick={() => setSelectedGuide(guide)}>
                <div className="db-guide-icon">{guide.icon}</div>
                <div className="db-guide-info">
                  <h4>{guide.title[lang] || guide.title.en}</h4>
                  <p>{guide.description[lang] || guide.description.en}</p>
                </div>
                <span className="db-guide-arrow">→</span>
              </div>
            ))}
          </div>
          <div className="db-locked-section">
            <div className="db-locked-header">🔒 {lang === 'fr' ? 'Module Affaires — Pionnier requis' : 'Business Module — Pathfinder Required'}</div>
            {lockedAt('enterprise').map(guide => (
              <div key={guide.id} className="db-guide-card locked">
                <div className="db-guide-icon locked-icon">{guide.icon}</div>
                <div className="db-guide-info">
                  <h4>{guide.title[lang] || guide.title.en}</h4>
                  <p className="locked-desc">{lang === 'fr' ? 'Disponible avec Pionnier (39 500 XAF / an)' : 'Available with Pathfinder (39,500 XAF / year)'}</p>
                </div>
                <span className="db-lock-badge">🔒</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const PricesTab = () => (
    <div className="db-salary">
      <h2 className="db-section-title">💰 {lang === 'fr' ? 'Prix courants à Istanbul — 2026' : 'Everyday Prices in Istanbul — 2026'}</h2>
      <p className="db-section-subtitle">{lang === 'fr' ? 'Fourchettes typiques dans les quartiers centraux. Les zones très touristiques peuvent être plus chères.' : 'Typical ranges in central districts. Very touristy spots can cost more.'}</p>
      <div className="db-table-wrap">
        <table className="db-table">
          <thead>
            <tr>
              <th>{lang === 'fr' ? 'Article' : 'Item'}</th>
              <th>{lang === 'fr' ? 'Prix (TL)' : 'Price (TL)'}</th>
              <th>≈ XAF</th>
            </tr>
          </thead>
          <tbody>
            {PRICE_DATA.map((row, i) => (
              <tr key={i}>
                <td><strong>{row.item[lang] || row.item.en}</strong></td>
                <td className="db-salary-amt">{row.tl}</td>
                <td>{row.xaf}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="db-data-note">📊 {lang === 'fr' ? 'Taux : 1 TL ≈ 11,7 XAF (sept. 2026). Tarifs transport officiels (fév. 2026) ; autres prix = estimations, l\'inflation les fait monter régulièrement.' : 'Rate: 1 TL ≈ 11.7 XAF (Sept 2026). Transport fares are official (Feb 2026); other prices are estimates and rise regularly with inflation.'}</p>
    </div>
  );

  const BudgetTab = () => {
    const style = BUDGET_STYLES[budget.style];
    const days = Math.max(1, Math.min(60, Number(budget.days) || 1));
    const people = Math.max(1, Math.min(20, Number(budget.people) || 1));
    const dailyEur = style.eurPerDay * days * people;
    const chosen = BUDGET_EXTRAS.filter(x => budget.extras[x.id]);
    const extrasEur = chosen.reduce((s, x) => s + x.eur, 0) * people;
    const totalEur = dailyEur + extrasEur;
    const row = (label, eur) => (
      <tr><td>{label}</td><td className="db-salary-amt">{fmt(eur * RATES.TL_PER_EUR)} TL</td><td>€{fmt(eur)}</td><td>{fmt(eur * RATES.XAF_PER_EUR)} XAF</td></tr>
    );
    return (
      <div className="db-salary">
        <h2 className="db-section-title">🧮 {lang === 'fr' ? 'Calculateur de budget Istanbul' : 'Istanbul Budget Calculator'}</h2>
        <p className="db-section-subtitle">{lang === 'fr' ? 'Estimation sur place (hors vols internationaux et frais de visa). Taux : 1 € ≈ 56 TL ≈ 656 XAF.' : 'On-the-ground estimate (excludes international flights and visa fees). Rate: €1 ≈ 56 TL ≈ 656 XAF.'}</p>

        <div className="db-tool-grid">
          <div className="db-tool-row">
            <label htmlFor="b-days">{lang === 'fr' ? 'Nombre de jours' : 'Number of days'}</label>
            <input id="b-days" className="db-tool-select" type="number" min="1" max="60" value={budget.days} onChange={e => setB({ days: e.target.value })} />
          </div>
          <div className="db-tool-row">
            <label htmlFor="b-people">{lang === 'fr' ? 'Voyageurs' : 'Travellers'}</label>
            <input id="b-people" className="db-tool-select" type="number" min="1" max="20" value={budget.people} onChange={e => setB({ people: e.target.value })} />
          </div>
        </div>

        <div className="db-roadmap-tabs">
          {Object.entries(BUDGET_STYLES).map(([id, s]) => (
            <button key={id} className={`db-rm-tab ${budget.style === id ? 'active' : ''}`} onClick={() => setB({ style: id })}>
              {s[lang] || s.en} — €{s.eurPerDay}/{lang === 'fr' ? 'jour' : 'day'}
            </button>
          ))}
        </div>
        <p className="db-panel-note">{style.desc[lang] || style.desc.en}</p>

        <div className="db-checklist-items">
          {BUDGET_EXTRAS.map(x => (
            <div key={x.id} className={`db-checklist-item ${budget.extras[x.id] ? 'done' : ''}`} onClick={() => setB({ extras: { ...budget.extras, [x.id]: !budget.extras[x.id] } })}>
              <div className={`db-checkbox premium ${budget.extras[x.id] ? 'checked' : ''}`}>{budget.extras[x.id] && '✓'}</div>
              <span>{x[lang] || x.en} — €{fmt(x.eur)}</span>
            </div>
          ))}
        </div>

        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Poste' : 'Item'}</th><th>TL</th><th>EUR</th><th>XAF</th></tr></thead>
            <tbody>
              {row(lang === 'fr' ? `Dépenses quotidiennes (${days} j × ${people} pers.)` : `Daily spending (${days} days × ${people} pers.)`, dailyEur)}
              {row(lang === 'fr' ? `Extras choisis (× ${people} pers.)` : `Selected extras (× ${people} pers.)`, extrasEur)}
              <tr><td><strong>{lang === 'fr' ? 'TOTAL ESTIMÉ' : 'ESTIMATED TOTAL'}</strong></td><td className="db-salary-amt"><strong>{fmt(totalEur * RATES.TL_PER_EUR)} TL</strong></td><td><strong>€{fmt(totalEur)}</strong></td><td><strong>{fmt(totalEur * RATES.XAF_PER_EUR)} XAF</strong></td></tr>
            </tbody>
          </table>
        </div>
        <p className="db-data-note">💡 {lang === 'fr' ? 'Pour le dossier de visa, l\'ambassade attend environ 50 € par jour et par personne sur vos relevés : ' : 'For the visa file, the embassy expects about €50 per day per person on your statements: '}<strong>{fmt(50 * days * people * RATES.XAF_PER_EUR)} XAF</strong>. {lang === 'fr' ? 'Ajoutez 10–15 % de marge pour l\'inflation et les imprévus.' : 'Add a 10–15% buffer for inflation and surprises.'}</p>
      </div>
    );
  };

  const ItineraryTab = () => {
    const days = [3, 5, 7].filter(d => d <= tripDays).flatMap(d => ITINERARY[d]);
    return (
      <div className="db-priority">
        <h2 className="db-section-title">📅 {lang === 'fr' ? 'Itinéraires jour par jour' : 'Day-by-Day Itineraries'}</h2>
        <div className="db-roadmap-tabs">
          {[3, 5, 7].map(d => (
            <button key={d} className={`db-rm-tab ${tripDays === d ? 'active' : ''}`} onClick={() => setTripDays(d)}>
              {d} {lang === 'fr' ? 'jours' : 'days'}
            </button>
          ))}
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Jour' : 'Day'}</th><th>{lang === 'fr' ? 'Programme' : 'Plan'}</th></tr></thead>
            <tbody>
              {days.map(d => (
                <tr key={d.day}><td><strong>{lang === 'fr' ? 'Jour' : 'Day'} {d.day}</strong></td><td>{d[lang] || d.en}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="db-bulletin-note">
          <strong>📌 {lang === 'fr' ? 'Règles d\'or' : 'Golden rules'}:</strong>
          <ol>
            <li>{lang === 'fr' ? 'Sainte-Sophie, Topkapı et la Citerne avant 10h' : 'Hagia Sophia, Topkapı and the Cistern before 10:00'}</li>
            <li>{lang === 'fr' ? 'Grand Bazar fermé le dimanche' : 'Grand Bazaar is closed on Sundays'}</li>
            <li>{lang === 'fr' ? 'Pas de visite de mosquée le vendredi 12h–14h' : 'No mosque visits on Friday 12:00–14:00'}</li>
            <li>{lang === 'fr' ? 'Traversez en ferry, pas en taxi, aux heures de pointe' : 'Cross the water by ferry, not taxi, at rush hour'}</li>
          </ol>
        </div>
      </div>
    );
  };

  const DistrictsTab = () => (
    <div className="db-states">
      <h2 className="db-section-title">🗺️ {lang === 'fr' ? 'Comparatif des quartiers' : 'District Comparison'}</h2>
      <p className="db-section-subtitle">{lang === 'fr' ? 'Prix hôtel = chambre double milieu de gamme par nuit (2026).' : 'Hotel price = mid-range double room per night (2026).'}</p>
      <div className="db-state-cards">
        {DISTRICT_DATA.map((s, i) => (
          <div key={i} className="db-state-card">
            <div className="db-state-header">
              <h4>{s.name}</h4>
              <span className="db-state-city">{pick(s.area, lang)}</span>
            </div>
            <div className="db-state-facts">
              <div className="db-state-fact">
                <span>{lang === 'fr' ? 'Hôtel' : 'Hotel'}</span>
                <strong>{s.hotel}</strong>
              </div>
              <div className="db-state-fact">
                <span>{lang === 'fr' ? 'Transport' : 'Transit'}</span>
                <strong>{pick(s.line, lang)}</strong>
              </div>
              <div className="db-state-fact">
                <span>{lang === 'fr' ? 'Idéal pour' : 'Best for'}</span>
                <strong className="db-notax">{pick(s.best, lang)}</strong>
              </div>
            </div>
            <p className="db-state-note">{pick(s.note, lang)}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const ChecklistTab = () => (
    <div className="db-checklist">
      <h2 className="db-section-title">{lang === 'fr' ? 'Liste de contrôle — Navigateur' : 'Checklist — Navigator'}</h2>
      <div className="db-progress-bar">
        <div className="db-progress-fill premium" style={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }} />
      </div>
      <p className="db-progress-label">{completedCount} / {CHECKLIST_ITEMS.length} {lang === 'fr' ? 'tâches complétées' : 'tasks completed'}</p>
      <div className="db-checklist-items">
        {CHECKLIST_ITEMS.map(item => (
          <div key={item.id} className={`db-checklist-item ${checked[item.id] ? 'done' : ''}`} onClick={() => toggleCheck(item.id)}>
            <div className={`db-checkbox premium ${checked[item.id] ? 'checked' : ''}`}>{checked[item.id] && '✓'}</div>
            <span>{item[lang] || item.en}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const ProfileTab = () => (
    <div className="db-profile">
      <div className="db-profile-card premium">
        <div className="db-profile-avatar">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
        <h2>{user?.firstName} {user?.lastName}</h2>
        <p>{user?.email}</p>
        <div className="db-plan-badge premium">{lang === 'fr' ? 'Forfait Navigateur' : 'Navigator Plan'}</div>
      </div>
      <div className="db-profile-details">
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Pays d\'origine' : 'Country of origin'}</span><strong>{user?.country || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Numéro de téléphone' : 'Phone number'}</span><strong>{user?.phone || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Forfait actuel' : 'Current plan'}</span><strong>{lang === 'fr' ? 'Navigateur (Premium)' : 'Navigator (Premium)'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Accès' : 'Access'}</span><strong>{lang === 'fr' ? '90 jours' : '90 days'}</strong></div>
      </div>
      <div className="db-profile-actions">
        <button className="db-upgrade-btn enterprise" onClick={() => navigate('/plans')}>{lang === 'fr' ? '⬆️ Passer à Pionnier' : '⬆️ Upgrade to Pathfinder'}</button>
        <button className="db-logout-btn" onClick={logout}>{lang === 'fr' ? 'Se Déconnecter' : 'Sign Out'}</button>
      </div>
    </div>
  );

  const renderTab = () => {
    switch (tab) {
      case 'overview':  return <OverviewTab />;
      case 'guides':    return <GuidesTab />;
      case 'budget':    return BudgetTab();
      case 'checklist': return <ChecklistTab />;
      case 'prices':    return <PricesTab />;
      case 'itinerary': return <ItineraryTab />;
      case 'districts': return <DistrictsTab />;
      case 'profile':   return <ProfileTab />;
      default:          return <OverviewTab />;
    }
  };

  return (
    <div className="db-layout">
      <aside className="db-sidebar premium">
        <div className="db-sidebar-brand">
          <span className="db-brand-icon">🕌</span>
          <span className="db-brand-text">{lang === 'fr' ? 'Guide Istanbul' : 'Istanbul Guide'}</span>
        </div>
        <nav className="db-nav">
          {TABS.map(t => (
            <button key={t.id} className={`db-nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span className="db-nav-icon">{t.icon}</span>
              <span>{t.label[lang] || t.label.en}</span>
            </button>
          ))}
        </nav>
        <div className="db-sidebar-footer">
          <button className="db-signout-btn" onClick={logout}>{lang === 'fr' ? 'Déconnexion' : 'Sign Out'}</button>
        </div>
      </aside>
      <main className="db-main">
        <div className="db-topbar">
          <h1 className="db-page-title">
            {TABS.find(t => t.id === tab)?.icon} {TABS.find(t => t.id === tab)?.label[lang]}
          </h1>
          <div className="db-user-chip premium">
            <div className="db-user-avatar">{user?.firstName?.[0]}</div>
            <span>{user?.firstName}</span>
          </div>
        </div>
        <div className="db-content">
          {renderTab()}
        </div>
      </main>
    </div>
  );
};

export default PremiumDashboard;
